// Shared, framework-free helpers for the redesigned EMA/Kareo chart modals
// (Send to Chart and Create New Chart). Each modal computes its own list of
// still-missing required fields (the rules differ — tasks/authorization vs
// insurance/gender) and renders an identical footer prompt from it.

// "a", "a and b", or "a, b, and c" — Oxford-comma join for the missing-field summary.
export const joinWithAnd = (items) => {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

// Footer "what's left" prompt for a disabled Submit. `closing` is the trailing
// clause (e.g. 'to send.' or 'to continue.'). Returns null when nothing is missing.
export const buildMissingMessage = (items, closing) => {
  if (!items || items.length === 0) return null;
  return `Add ${joinWithAnd(items)} ${closing}`;
};

// A chart modal's form can outlive its turn on screen: Next/Previous Record swaps it
// for the next record's (reducers/modal) while its submit is still in flight, and that
// submit calls hideModal when it finishes. Bind each form's hideModal to the modal it
// was opened as, so a late callback can't close the form that replaced it.
export const hideOwnModalOnly = (ownModal, getOpenModal, hideModal) => () => {
  if (getOpenModal() === ownModal) hideModal();
};

// EMA insurance plans (Create New Chart). A payer's plans come from the daily EMA sync;
// a payer the practice has set up no plans for has none, and the modal shows no plan
// field for it. The plan is always optional.
const planKey = (value) => (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const planOptionsFor = (payerPlanNamesMap, payer) => (
  (payer && (payerPlanNamesMap || {})[payer]) || []
);

// The catalogue spelling of `planName` among the payer's plans, or '' when it is not one
// of them. Only catalogue plans are submitted: the field can be prefilled with AI-extracted
// plan text that EMA would otherwise store verbatim.
export const catalogPlanName = (payerPlanNamesMap, payer, planName) => {
  const key = planKey(planName);
  if (!key) return '';
  const match = planOptionsFor(payerPlanNamesMap, payer).find((plan) => planKey(plan.name) === key);
  return match ? match.name : '';
};

// What the plan field should become when the payer or the plan catalogue changes, or null
// to leave it alone: a prefilled value that matches a plan takes the catalogue spelling, and
// a payer with exactly one plan gets it selected. Picking a plan fills the policy type from
// it, the way EMA's own form does; a plan without one leaves the policy type untouched.
export const planSelectionFor = (payerPlanNamesMap, payer, currentPlanName) => {
  const options = planOptionsFor(payerPlanNamesMap, payer);
  if (options.length === 0) return null;

  const matched = catalogPlanName(payerPlanNamesMap, payer, currentPlanName);
  let plan = null;
  if (matched) {
    plan = options.find((option) => option.name === matched);
  } else if (options.length === 1) {
    [plan] = options;
  }
  if (!plan || (plan.name === currentPlanName && !plan.policy_type)) return null;

  return { planName: plan.name, policyType: plan.policy_type || null };
};

// In an org with several EMA logins, a payer's plans come from the one login whose
// catalogue owns that payer. When the chart is bound for a chosen login, keep only the
// payers that login owns, so another instance's plans are never offered or auto-selected.
// Without a chosen destination the map is returned as is.
export const plansForDestination = (payerPlanNamesMap, payerEmaCredentialMap, destinationId) => {
  const plans = payerPlanNamesMap || {};
  if (destinationId === null || destinationId === undefined) return plans;
  const owners = payerEmaCredentialMap || {};
  return Object.fromEntries(Object.entries(plans).filter(([payer]) => (
    owners[payer] !== null && owners[payer] !== undefined && Number(owners[payer]) === Number(destinationId))));
};

// State changes when staff pick or clear a plan. A plan with a policy type fills and
// locks Policy Type, remembering what was there before; otherwise a Policy Type that an
// earlier plan filled goes back to that value, and one staff chose themselves is kept.
export const planChangeUpdates = (prefix, value, selectedPlan, state) => {
  const policyType = selectedPlan ? selectedPlan.policy_type : null;
  const updates = {
    [`${prefix}PlanName`]: value || '',
    [`${prefix}PolicyTypeLocked`]: Boolean(policyType),
  };
  if (policyType) {
    if (!state[`${prefix}PolicyTypeLocked`]) {
      updates[`${prefix}PolicyTypeBeforePlan`] = state[`${prefix}PolicyType`];
    }
    updates[`${prefix}PolicyType`] = policyType;
  } else if (state[`${prefix}PolicyTypeLocked`] && state[`${prefix}PolicyTypeBeforePlan`] !== undefined) {
    updates[`${prefix}PolicyType`] = state[`${prefix}PolicyTypeBeforePlan`];
  }
  return updates;
};

// Puts both Policy Types on the default when insurance is turned on. A Policy Type a
// plan filled stays locked: the default becomes the value clearing that plan restores.
// The payer catalogue can arrive and auto-select a plan before the modal finishes
// opening, so this runs against the latest state rather than overwriting it.
export const defaultPolicyTypeUpdates = (state, defaultPolicyType = 'Commercial - Other') => {
  const updates = {};
  ['primary', 'secondary'].forEach((prefix) => {
    if (state[`${prefix}PolicyTypeLocked`]) {
      updates[`${prefix}PolicyTypeBeforePlan`] = defaultPolicyType;
    } else {
      updates[`${prefix}PolicyType`] = defaultPolicyType;
    }
  });
  return updates;
};

// Analytics for staff picking or clearing a plan (never for auto-selection). Plan and
// payer names stay out: only which insurance, which modal, and the plan's effect.
export const planChangeAnalytics = (prefix, value, selectedPlan, planCount, modal) => [
  value ? 'ema_chart_plan_name_selected' : 'ema_chart_plan_name_cleared',
  {
    insurance: prefix,
    modal,
    plan_count: planCount,
    fills_policy_type: Boolean(selectedPlan && selectedPlan.policy_type),
  },
];
